import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { BannersDataService, BannersColumnsService } from '../../services';
import { DialogBannerFormComponent } from './banner-form/banner-form.component';
import { DialogBannerDetailsComponent } from './banner-details/banner-details.component';
import { Banner, BannersResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-banners',
  templateUrl: './banners.component.html',
  styleUrls: ['./banners.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class BannersComponent implements OnInit, OnDestroy {

  private bannerAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private bannerEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private bannerDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public bannerToDelete: Banner = {};
  public banners: Banner[] = [];
  public banner: Banner = {};
  public selectedBanners: Banner[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private bannersDataService: BannersDataService,
    private bannersColumnsService: BannersColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('BannersComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Banners - Management');
    this.primengConfig.ripple = true;
    this.getBanners();
  }

  ngOnDestroy(): void {
    console.log('BannersComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getBanners(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.bannersDataService.getBanners(this.parameters).subscribe((response: BannersResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.banners = response.results;
        } else {
          this.banners = this.banners.concat(response.results);
        }
        if (response.inlineCount > this.banners.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getBanners();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('BANNERSNOTLOADED')});
    });
  }

  importBanners(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.bannersDataService.addBanners(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('BANNERSCREATED')});
                if (proceed === j) {
                  this.getBanners();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('BANNERSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('BANNERSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.bannersDataService.updateBanners(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('BANNERSUPDATED')});
                if (proceed === j) {
                  this.getBanners();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('BANNERSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('BANNERSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.bannersColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('banners.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedBanners() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Banners?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.bannersDataService.deleteBanners(this.selectedBanners).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Banners Deleted', life: 3000});
            this.getBanners();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Banners not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Banners not deleted'});
        });
        this.selectedBanners = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.banners, this.columns, 'Banners');
  }

  exportExcel() {
    ToolsService.exportExcel(this.banners, this.columns, 'Banners');
  }

  exportCsv() {
    ToolsService.exportCsv(this.banners, this.columns, 'Banners');
  }

  refresh() {
    this.bannersDataService.clearCache();
    this.getBanners();
  }

  bannerAdd(): void {
    if (!this.navigation.getParamValue('banners.addInDialog', true)) {
      this.router.navigate(['/banners/add']);
      return;
    }
    this.bannerAddDialog = this.dialogService.open(DialogBannerFormComponent, {
      header: '', // this.translate.instant('NEWBANNER'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        banner: {id: null},
        bannerTypes: this.bannersColumnsService.bannerTypes,
        users: this.bannersColumnsService.users,
        bannerPositions: this.bannersColumnsService.bannerPositions,
      },
    });

    this.bannerAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Banner added'});
        this.getBanners();
      }
    });
  }

  bannerEdit(banner: Banner): void {
    if (!this.navigation.getParamValue('banners.editInDialog', true)) {
      this.router.navigate(['/banners/edit/' + banner.id]);
      return;
    }
    this.bannerEditDialog = this.dialogService.open(DialogBannerFormComponent, {
      header: '', // this.translate.instant('EDITBANNER') + ' ' + banner.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        banner: banner,
        bannerTypes: this.bannersColumnsService.bannerTypes,
        users: this.bannersColumnsService.users,
        bannerPositions: this.bannersColumnsService.bannerPositions,
      },
    });

    this.bannerEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Banner updated'});
        this.getBanners();
      }
    });
  }

  bannerDetails(banner: Banner): void {
    if (!this.navigation.getParamValue('banners.detailsInDialog', true)) {
      this.router.navigate(['/banners/details/' + banner.id]);
      return;
    }
    this.bannerDetailsDialog = this.dialogService.open(DialogBannerDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        banner: banner,
      }
    });
  }

  bannerDelete(banner: Banner): void {
    this.bannerToDelete = banner;
    this.messageService.clear();
    this.messageService.add({
      key: 'bannerDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + banner.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.bannersDataService.deleteBanner(this.bannerToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Banner deleted'});
      this.getBanners();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Banner not deleted'});
    });
  }

  onDeleteReject(): void {
    this.bannerToDelete = {};
    this.messageService.clear('bannerDelete');
  }

}
